import { useMemo } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import { useParams } from "react-router-dom";
import CalendarIcon from "assets/icons/medium/calendar.svg?react";
import ComputerIcon from "assets/icons/medium/computer.svg?react";
import DisbursementsIcon from "assets/icons/medium/disbursements.svg?react";
import FileIcon from "assets/icons/medium/file.svg?react";
import HomeIcon from "assets/icons/medium/house.svg?react";
import ListingAlternativeIcon from "assets/icons/medium/listing-alternative.svg?react";
import PeopleIcon from "assets/icons/medium/people.svg?react";
import type { MenuItem } from "components/left-navigation-menu/components/left-navigation-menu";
import { LeftNavigationMenu } from "components/left-navigation-menu/components/left-navigation-menu";
import { useAgencySettings } from "contexts/agency-settings-provider";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import { VENDOR_PORTAL_PATHS } from "modules/vendor-portal/paths";
import { lockedRouteConfig } from "modules/vendor-portal/utils/locked-routes-config";
import { getRoleFromToken } from "utils/auth";

const menuItems: MenuItem[] = [
  {
    label: "Overview",
    path: VENDOR_PORTAL_PATHS.OVERVIEW,
    Icon: HomeIcon,
  },
  {
    label: "Events",
    path: VENDOR_PORTAL_PATHS.EVENTS,
    Icon: CalendarIcon,
    isLockedTooltip:
      "Once appointments are scheduled for your property, you’ll see the full list of upcoming events here.",
  },
  {
    label: "Feedback",
    path: VENDOR_PORTAL_PATHS.FEEDBACK,
    Icon: PeopleIcon,
    isLockedTooltip:
      "Once we start receiving enquiries and feedback, this section will become available for you to view everything in one spot",
  },
  {
    label: "Performance",
    path: VENDOR_PORTAL_PATHS.PERFORMANCE,
    Icon: ComputerIcon,
    isLockedTooltip:
      "Once we launch your campaign and it’s live on the portals, you will be able to view campaign statistics here.",
  },
  {
    label: "Offers",
    path: VENDOR_PORTAL_PATHS.OFFERS,
    Icon: DisbursementsIcon,
    featureKey: "vendor_portal.navigation.show_offers",
    isLockedTooltip:
      "Once you receive an offer, it will appear here along with any others that follow.",
  },
  {
    label: "Marketing",
    path: VENDOR_PORTAL_PATHS.MARKETING,
    Icon: ListingAlternativeIcon,
    isLockedTooltip:
      "This is where you’ll find the advertising content your agent has prepared for your property.",
  },
  {
    label: "Documents",
    path: VENDOR_PORTAL_PATHS.DOCUMENTS,
    Icon: FileIcon,
    isLockedTooltip:
      "Any documents shared with you by your agent will appear here for easy access.",
  },
];

export function LeftNavigation() {
  const { propertyId } = useParams();
  const { data: listing } = useSuspenseQuery(
    listingsModel.queries.read(propertyId),
  );
  const { preferences } = useAgencySettings();

  const ww = useWhichWordFactory();

  const navBarItems = useMemo<MenuItem[]>(() => {
    return menuItems.map((item) => {
      const { isRouteLocked } =
        lockedRouteConfig[item.path as keyof typeof lockedRouteConfig] ?? {};

      return {
        ...item,
        label: ww(item.label),
        isLocked: isRouteLocked ? isRouteLocked(listing, preferences) : false,
      };
    });
  }, [ww, listing, preferences]);

  return (
    <LeftNavigationMenu
      items={navBarItems}
      hideListingSelector={
        !!getRoleFromToken() && getRoleFromToken() !== "vendor"
      }
    />
  );
}
